import type {
  AuthContextValue,
  AuthOrganization,
  AuthPasswordLoginResult,
  NewPasswordChallengeInput,
  TwoFactorChallengeInput,
} from '@/app/providers/auth-context'
import {
  completeNewPasswordChallenge as completeNewPasswordChallengeApi,
  completeTwoFactorChallenge as completeTwoFactorChallengeApi,
  isPasswordLoginChallenge,
  loginWithPassword as loginWithPasswordApi,
  setPasswordWithToken as setPasswordWithTokenApi,
} from '@/features/auth/api'
import type { SessionTokens } from '@/lib/auth/tokenStore'

type PasswordSessionDoors = Pick<
  AuthContextValue,
  'loginWithPassword' | 'setPasswordWithToken' | 'completeNewPasswordChallenge' | 'completeTwoFactorChallenge'
>

/**
 * THE PASSWORD-SHAPED WAYS IN, and the one rule they share.
 *
 * Signing in, activating from a set-password link, finishing an invited sign-in
 * and answering a two-factor code all get their session from one backend helper
 * (buildSessionResponse), so each must land in identical provider state: every
 * token set they produce goes through the provider's `adoptSession`, and nothing
 * here opens a second path to storage. A challenge is not a session — step 1
 * hands it straight back with nothing stored, no profile fetched and nobody
 * signed in.
 *
 * Lifted out of AuthProvider (287 of 300 lines) when the fourth door arrived.
 */
export function passwordSessionDoors(
  adoptSession: (tokens: SessionTokens) => Promise<AuthOrganization[]>,
): PasswordSessionDoors {
  async function loginWithPassword(email: string, password: string): Promise<AuthPasswordLoginResult> {
    const outcome = await loginWithPasswordApi({ email, password })
    if (isPasswordLoginChallenge(outcome)) return outcome
    return { organizations: await adoptSession(outcome.tokens) }
  }

  async function setPasswordWithToken(token: string, password: string): Promise<void> {
    // Token error / weak password propagate as ApiError to the form.
    await adoptSession(await setPasswordWithTokenApi({ token, password }))
  }

  async function completeNewPasswordChallenge(input: NewPasswordChallengeInput): Promise<AuthOrganization[]> {
    return adoptSession(await completeNewPasswordChallengeApi(input))
  }

  async function completeTwoFactorChallenge(input: TwoFactorChallengeInput): Promise<AuthOrganization[]> {
    return adoptSession(await completeTwoFactorChallengeApi(input))
  }

  return { loginWithPassword, setPasswordWithToken, completeNewPasswordChallenge, completeTwoFactorChallenge }
}
